import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { LowStockCard } from "../../components/LowStockCard";
import {
  ShoppingBag,
  IndianRupee,
  Store as StoreIcon,
  Users,
  Package,
  TrendingUp,
  Target,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";

const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

function Kpi({ icon: Icon, label, value, sub, tone = "primary" }) {
  const toneMap = {
    primary: "bg-[color:var(--primary)]/10 text-[color:var(--primary)]",
    moss: "bg-[color:var(--moss)]/10 text-[color:var(--moss)]",
    mustard: "bg-[color:var(--mustard)]/15 text-[#9A742E]",
    ink: "bg-[color:var(--ink)]/5 text-[color:var(--ink)]",
  };
  return (
    <div className="rounded-xl border border-[color:var(--border)] bg-white p-5 ink-shadow" data-testid={`kpi-${label}`}>
      <div className="flex items-center justify-between">
        <span className="overline text-[color:var(--muted)]">{label}</span>
        <span className={`h-9 w-9 rounded-lg flex items-center justify-center ${toneMap[tone]}`}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <div className="mt-3 font-display text-3xl font-bold text-[color:var(--ink)]">{value}</div>
      {sub && <div className="text-xs text-[color:var(--muted)] mt-1">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const nowIso = new Date().toISOString();
  const currentMonth = `${nowIso.slice(0, 7)}`; // YYYY-MM

  const [month, setMonth] = useState(currentMonth);
  const [stats, setStats] = useState(null);
  const [targets, setTargets] = useState(null);

  // Build last 12 months dropdown options, newest first
  const monthOptions = Array.from({ length: 12 }).map((_, i) => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - i);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
    return { value: val, label };
  });

  useEffect(() => {
    setStats(null);
    api
      .get("/dashboard/stats", { params: { month } })
      .then((r) => setStats(r.data))
      .catch(() => {});
    api.get("/targets/salesmen").then((r) => setTargets(r.data)).catch(() => {});
  }, [month]);

  const isCurrentMonth = month === currentMonth;
  const monthLabel =
    monthOptions.find((m) => m.value === month)?.label || month;

  if (!stats) {
    return <div className="text-[color:var(--muted)]">Loading dashboard…</div>;
  }

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="overline text-[color:var(--muted)]">Overview</p>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-1">
            Distribution command center
          </h1>
          <p className="text-[color:var(--muted)] mt-1">
            {monthLabel} {isCurrentMonth ? "· running month" : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="overline text-[color:var(--muted)] mr-1">Month</label>
          <select
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="h-10 px-3 rounded-lg border border-[color:var(--border)] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--primary)]/30"
            data-testid="dashboard-month-select"
          >
            {monthOptions.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
          {!isCurrentMonth && (
            <button
              onClick={() => setMonth(currentMonth)}
              className="h-10 px-3 rounded-lg border border-[color:var(--border)] bg-white text-xs font-semibold uppercase tracking-wide text-[color:var(--ink)] hover:bg-[color:var(--surface-2)]"
              data-testid="dashboard-month-reset"
            >
              This month
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        <Kpi icon={IndianRupee} label="Revenue" value={fmtINR(stats.total_revenue)} tone="primary" />
        <Kpi icon={ShoppingBag} label="Orders" value={stats.total_orders} tone="moss" />
        <Kpi icon={StoreIcon} label="Stores" value={stats.total_stores} tone="mustard" />
        <Kpi icon={Users} label="Salesmen" value={stats.total_salesmen} tone="ink" />
        <Kpi icon={Package} label="Products" value={stats.total_products} tone="moss" />
        <Kpi icon={TrendingUp} label="Avg Order" value={fmtINR(stats.total_orders ? stats.total_revenue / stats.total_orders : 0)} tone="primary" />
      </div>

      <LowStockCard />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-xl border border-[color:var(--border)] bg-white p-5 ink-shadow">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="overline text-[color:var(--muted)]">
                {isCurrentMonth ? "Month to date" : monthLabel}
              </p>
              <h3 className="font-display text-xl font-bold">Daily revenue</h3>
            </div>
          </div>
          <div className="h-64" data-testid="daily-revenue-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.daily}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7E5DF" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => fmtINR(v)} />
                <Bar dataKey="revenue" fill="#B94A3E" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-xl border border-[color:var(--border)] bg-white p-5 ink-shadow">
          <p className="overline text-[color:var(--muted)]">Top performers</p>
          <h3 className="font-display text-xl font-bold mb-4">Salesmen</h3>
          <div className="space-y-3">
            {stats.top_salesmen.length === 0 && (
              <div className="text-sm text-[color:var(--muted)]">No sales yet.</div>
            )}
            {stats.top_salesmen.map((s, i) => (
              <div key={s.salesman_id} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-full bg-[color:var(--moss)]/10 text-[color:var(--moss)] flex items-center justify-center text-sm font-semibold">
                    {i + 1}
                  </div>
                  <div>
                    <div className="text-sm font-medium">{s.name}</div>
                    <div className="text-xs text-[color:var(--muted)]">{s.orders} orders</div>
                  </div>
                </div>
                <div className="text-sm font-semibold">{fmtINR(s.revenue)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-[color:var(--border)] bg-white p-5 ink-shadow">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="overline text-[color:var(--muted)]">This month</p>
            <h3 className="font-display text-xl font-bold flex items-center gap-2">
              <Target className="h-5 w-5 text-[color:var(--primary)]" /> Monthly targets
            </h3>
            {targets?.month && (
              <p className="text-xs text-[color:var(--muted)] mt-0.5">{targets.month}</p>
            )}
          </div>
        </div>
        {!targets || targets.rows.length === 0 ? (
          <div className="text-sm text-[color:var(--muted)] py-6 text-center">
            No salesmen with targets configured yet. Set them on the Salesmen page.
          </div>
        ) : (
          <div className="space-y-3" data-testid="admin-targets-list">
            {targets.rows.map((r) => {
              const hasTarget = r.target > 0;
              const tone =
                r.pct >= 100
                  ? "bg-[color:var(--moss)]"
                  : r.pct >= 60
                  ? "bg-[color:var(--mustard)]"
                  : "bg-[color:var(--primary)]";
              return (
                <div
                  key={r.salesman_id}
                  className="flex items-center gap-4"
                  data-testid={`target-row-${r.salesman_id}`}
                >
                  <div className="w-32 sm:w-44 shrink-0">
                    <div className="text-sm font-medium truncate">{r.name}</div>
                    <div className="text-xs text-[color:var(--muted)] truncate">
                      {hasTarget ? `${r.days_left}d left` : "No target set"}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="tabular-nums text-[color:var(--ink)] font-semibold">
                        {fmtINR(r.achieved)}
                      </span>
                      {hasTarget && (
                        <span className="tabular-nums text-[color:var(--muted)]">
                          / {fmtINR(r.target)}
                        </span>
                      )}
                    </div>
                    <div className="h-2 rounded-full bg-[color:var(--surface-2)] overflow-hidden">
                      {hasTarget && (
                        <div
                          className={`h-full rounded-full transition-all ${tone}`}
                          style={{ width: `${Math.min(100, r.pct)}%` }}
                        />
                      )}
                    </div>
                  </div>
                  <div className="w-14 text-right text-sm font-semibold tabular-nums">
                    {hasTarget ? `${r.pct}%` : "—"}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="rounded-xl border border-[color:var(--border)] bg-white p-5 ink-shadow">
        <p className="overline text-[color:var(--muted)]">Revenue leaderboard</p>
        <h3 className="font-display text-xl font-bold mb-4">Top stores</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[color:var(--muted)] uppercase text-xs">
                <th className="py-2">#</th>
                <th className="py-2">Store</th>
                <th className="py-2">Orders</th>
                <th className="py-2 text-right">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {stats.top_stores.length === 0 && (
                <tr>
                  <td colSpan="4" className="py-6 text-center text-[color:var(--muted)]">
                    No store revenue yet.
                  </td>
                </tr>
              )}
              {stats.top_stores.map((s, i) => (
                <tr key={s.store_id} className="border-t border-[color:var(--border)]">
                  <td className="py-3">{i + 1}</td>
                  <td className="py-3 font-medium">{s.store_name}</td>
                  <td className="py-3">{s.orders}</td>
                  <td className="py-3 text-right font-semibold">{fmtINR(s.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
